/**
 * Elastic (WAF-log / bsearch) field registry — FE mirror of the backend
 * allow-list (`internal/domain/elastic_fields.go`). The backend builds the ES
 * aggregation ONLY from Dimensions/Metric/MetricField/Filters using these logical
 * names (a security boundary — arbitrary ES fields / free-text KQL are rejected),
 * so the create form is a structured builder over this registry, not a query box.
 *
 * `es` is the underlying Elasticsearch field (matches the waflog `_source` keys),
 * shown as a hint so users recognise their own log fields.
 */
export type ElasticFieldKind = 'keyword' | 'numeric';

export interface ElasticFieldDef {
  /** Logical name sent in the config (dimensions/filters/metric_field). */
  name: string;
  label: string;
  kind: ElasticFieldKind;
  /** Underlying ES field (waflog _source key) — shown as a hint. */
  es: string;
  group: string;
}

export const ELASTIC_FIELDS: ElasticFieldDef[] = [
  { name: 'domain', label: 'Domain', kind: 'keyword', es: 'request_host', group: 'Identity' },
  { name: 'remote_ip', label: 'Client IP (realip)', kind: 'keyword', es: 'realip_remote_addr', group: 'Identity' },
  { name: 'remote_addr', label: 'Remote address', kind: 'keyword', es: 'remote_addr', group: 'Identity' },
  { name: 'method', label: 'HTTP method', kind: 'keyword', es: 'request_method', group: 'Request' },
  { name: 'uri', label: 'URI path', kind: 'keyword', es: 'uri', group: 'Request' },
  { name: 'user_agent', label: 'User agent', kind: 'keyword', es: 'http_user_agent', group: 'Request' },
  { name: 'referer', label: 'Referer', kind: 'keyword', es: 'http_referer', group: 'Request' },
  { name: 'scheme', label: 'Scheme', kind: 'keyword', es: 'scheme', group: 'Request' },
  { name: 'mitigation', label: 'WAF mitigation', kind: 'keyword', es: 'mitigation_result', group: 'Security' },
  { name: 'country', label: 'Country', kind: 'keyword', es: 'geoip.country_name', group: 'Geo / ASN' },
  { name: 'country_code', label: 'Country code', kind: 'keyword', es: 'geoip.country_iso_code', group: 'Geo / ASN' },
  { name: 'city', label: 'City', kind: 'keyword', es: 'geoip.city_name', group: 'Geo / ASN' },
  { name: 'asn_org', label: 'ASN organisation', kind: 'keyword', es: 'asn.organization_name', group: 'Geo / ASN' },
  { name: 'asn', label: 'ASN', kind: 'numeric', es: 'asn.asn', group: 'Geo / ASN' },
  { name: 'waf_region', label: 'WAF region', kind: 'keyword', es: 'waf_region', group: 'Infra' },
  { name: 'cdn_provider', label: 'CDN provider', kind: 'keyword', es: 'cdn_provider', group: 'Infra' },
  { name: 'cdn_node_ip', label: 'CDN node IP', kind: 'keyword', es: 'cdn_node_ip', group: 'Infra' },
  { name: 'status', label: 'Status code', kind: 'numeric', es: 'status', group: 'Status' },
  { name: 'upstream_status', label: 'Upstream status', kind: 'numeric', es: 'upstream_status', group: 'Status' },
  { name: 'request_time', label: 'Request time (s)', kind: 'numeric', es: 'request_time', group: 'Metrics' },
  { name: 'upstream_response_time', label: 'Upstream response time (s)', kind: 'numeric', es: 'upstream_response_time', group: 'Metrics' },
  { name: 'upstream_connect_time', label: 'Upstream connect time (s)', kind: 'numeric', es: 'upstream_connect_time', group: 'Metrics' },
  { name: 'bytes_sent', label: 'Bytes sent', kind: 'numeric', es: 'bytes_sent', group: 'Metrics' },
  { name: 'body_bytes_sent', label: 'Body bytes sent', kind: 'numeric', es: 'body_bytes_sent', group: 'Metrics' },
  { name: 'request_length', label: 'Request length', kind: 'numeric', es: 'request_length', group: 'Metrics' },
];

export const ELASTIC_FIELD_BY_NAME: Record<string, ElasticFieldDef> = Object.fromEntries(
  ELASTIC_FIELDS.map((f) => [f.name, f]),
);

/** Fields usable as a group-by dimension (discrete: keywords + a few numerics). */
export const ELASTIC_DIMENSION_FIELDS = ELASTIC_FIELDS.filter(
  (f) => f.kind === 'keyword' || ['status', 'upstream_status', 'asn'].includes(f.name),
);

/** Numeric fields usable as the metric target (avg/sum/p95/...). */
export const ELASTIC_METRIC_FIELDS = ELASTIC_FIELDS.filter((f) => f.kind === 'numeric');

export const ELASTIC_METRICS = ['count', 'avg', 'sum', 'max', 'min', 'p95', 'p99'] as const;
export type ElasticMetric = (typeof ELASTIC_METRICS)[number];

export const MAX_ELASTIC_DIMENSIONS = 3;

/** Quick-start presets — fill dimensions/metric/filters for common questions. */
export interface ElasticTemplate {
  id: string;
  label: string;
  description: string;
  patch: {
    dimensions?: string[];
    metric?: ElasticMetric;
    metric_field?: string;
    filters?: Record<string, unknown>;
  };
}

export const ELASTIC_TEMPLATES: ElasticTemplate[] = [
  { id: 'country', label: 'Traffic by country', description: 'Request count grouped by country code.', patch: { dimensions: ['country_code'], metric: 'count', metric_field: '', filters: {} } },
  { id: 'top-uri', label: 'Top URIs', description: 'Most requested URI paths.', patch: { dimensions: ['uri'], metric: 'count', metric_field: '', filters: {} } },
  { id: '5xx', label: '5xx errors by URI', description: 'Server errors grouped by URI.', patch: { dimensions: ['uri'], metric: 'count', metric_field: '', filters: { status: [500, 502, 503, 504] } } },
  { id: 'mitigation', label: 'WAF mitigations', description: 'Requests grouped by mitigation result.', patch: { dimensions: ['mitigation'], metric: 'count', metric_field: '', filters: {} } },
  { id: 'latency', label: 'P95 latency by domain', description: '95th-percentile upstream response time.', patch: { dimensions: ['domain'], metric: 'p95', metric_field: 'upstream_response_time', filters: {} } },
  { id: 'top-ip', label: 'Top client IPs', description: 'Busiest client IPs.', patch: { dimensions: ['remote_ip'], metric: 'count', metric_field: '', filters: {} } },
];
